import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { setIn, useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";
import { Edit, PhotoCamera } from "@mui/icons-material";
import EmployeeAvatar from "components/EmployeeAvatar";
import ConfirmationDialog from "components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { getJobGroupsByJobId } from "services/api";
import { fieldStyles } from "services/utils";
import MDButton from "components/MDButton";
import { resetPassword } from "services/api";
import { useNavigate } from "react-router-dom";

const AddEmployeeDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  jobs,
  employees,
  branches,
  employee,
  departments,
  defaultJobId,
}) => {
  const { t } = useTranslation();
  const [directManagers, setDirectManagers] = useState([]);
  const [indirectManagers, setIndirectManagers] = useState([]);
  const [department, setDepartment] = useState();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [jobGroups, setJobGroups] = useState([]);
  const [loadingJobGroups, setLoadingJobGroups] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [severity, setSeverity] = useState("error");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const confirmClose = () => {
    setConfirmOpen(false);
  };

  // **Validation Schema**
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(t("admin.employees.employeeDialog.validation.nameRequired"))
      .test(
        "unique-name",
        t("admin.employees.employeeDialog.validation.nameUnique"),
        (value) =>
          !employees.some(
            (e) => e.id != employee?.id && e.name.toLowerCase() === value.toLowerCase()
          )
      ),
    email: Yup.string()
      .email(t("admin.employees.employeeDialog.validation.emailInvalid"))
      .required(t("admin.employees.employeeDialog.validation.emailRequired"))
      .test(
        "unique-email",
        t("admin.employees.employeeDialog.validation.emailUnique"),
        (value) =>
          !employees.some(
            (e) => e.id != employee?.id && e.email.toLowerCase() === value.toLowerCase()
          )
      ),
    dob: Yup.date().nullable(),
    isAdmin: Yup.string().required(t("admin.employees.employeeDialog.validation.roleRequired")),
    employmentType: Yup.string().required(""),
    gender: Yup.string().required(t("admin.employees.employeeDialog.validation.genderRequired")),
    branch: Yup.string().required(t("admin.employees.employeeDialog.validation.branchRequired")),
    job: Yup.string().required(t("admin.employees.employeeDialog.validation.jobRequired")),
    directManager: Yup.string().nullable().optional(),
    indirectManager: Yup.string().nullable().optional(),
    jobGroup: Yup.string().nullable().optional(),
    phoneNumber: Yup.string()
      .nullable()
      .optional()
      .matches(/^\d+$/, t("admin.employees.employeeDialog.validation.phoneNumeric")), // ✅ Only numbers allowed

    referencedEmployeeId: Yup.number()
      .nullable()
      .optional()
      .test(
        "unique-referencedEmployeeId",
        t("admin.employees.employeeDialog.validation.employeeIdUnique"),
        (value) =>
          !value || !employees.some((e) => e.id != employee?.id && e.referencedEmployeeId === value) // ✅ Must be unique if present
      ),
  });

  const formik = useFormik({
    initialValues: {
      name: employee ? employee.name : "",
      email: employee ? employee.email : "",
      dob: employee ? employee.dob : null,
      isAdmin: employee ? employee.isAdmin : "",
      branch: employee ? employee.branchId : "",
      job: employee ? employee.jobId : defaultJobId ? defaultJobId : "",
      gender: employee ? employee.gender : "",
      phoneNumber: employee ? employee.phoneNumber : "",
      referencedEmployeeId: employee ? employee.referencedEmployeeId : "",
      directManager: employee ? employee.directManagerId : "",
      indirectManager: employee ? employee.indirectManagerId : "",
      photo: employee ? employee.photo : null,
      jobGroup: employee ? employee.jobGroupId : "",
      employmentType: employee ? employee.employmentType.trim() : "FULL_TIME",
    },
    enableReinitialize: true, // Ensure form updates when employee changes
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      await onSave(values, department.departmentId);
      formik.resetForm();
      setDirectManagers([]);
      setIndirectManagers([]);
      setDepartment();
      onClose();
      setLoading(false);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setDirectManagers([]);
    setIndirectManagers([]);
    setDepartment();
    setJobGroups([]);
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Validate file type (only images)
      if (!file.type.startsWith("image/")) {
        setSnackbarMessage(t("admin.employees.employeeDialog.validation.photoInvalidType"));
        setSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 2 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setSnackbarMessage(t("admin.employees.employeeDialog.validation.photoTooLarge"));
        setSeverity("error");

        setSnackbarOpen(true);
        return;
      }

      // Read the file as Base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1]; // Remove prefix
        formik.setFieldValue("photo", base64String); // Store Base64 string
      };
      reader.readAsDataURL(file);
    }
  };
  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // **Handle Job Selection (Update Direct Managers)**
  useEffect(() => {
    if (formik.values.job) {
      const selectedJobId = formik.values.job;

      if (selectedJobId) {
        setLoadingJobGroups(true);
        getJobGroupsByJobId(selectedJobId)
          .then((data) => {
            if (data != "error") setJobGroups(data);
          })
          .catch((err) => {
            setJobGroups([]);
          })
          .finally(() => {
            setLoadingJobGroups(false);
          });
      } else {
        setJobGroups([]);
      }
      const selectedJob = jobs?.find((j) => j.jobId === formik.values.job);
      if (selectedJob) {
        const managers = employees.filter(
          (e) => e.id != employee?.id && e.jobId === selectedJob.reportingToSolidId
        );
        setDirectManagers(managers);
        const indirectManag = employees.filter(
          (e) => e.id != employee?.id && e.jobId === selectedJob.reportingToDottedId
        );
        setIndirectManagers(indirectManag);
        const departm = departments?.find((d) => d.departmentId === selectedJob.departmentId);
        setDepartment(departm);
      } else {
        setDirectManagers([]);
        setIndirectManagers([]);
        setDepartment("");
      }
    }
  }, [formik.values.job, jobs, employees]);

  const handleOpenEmpDetails = () => {
    navigate("/employee-details", {
      state: {
        employees,
        employeeId: employee?.id,
        departments,
      },
    });
  };

  const handleResetPassword = async () => {
    const res = await resetPassword(employee?.id);
    if (res != "error") {
      setSnackbarMessage(t("admin.employees.employeeDialog.validation.passwordReset"));
      setSeverity("success");

      setSnackbarOpen(true);
    }
    setResetPasswordConfirmOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#333" : "#fff", // Changes only the dialog background
          color: darkMode ? "#fff" : "#000", // Ensures text color is visible
          borderRadius: 2, // Optional: Smooth rounded corners
          padding: 2, // Optional: Adds some spacing inside
        },
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {employee
          ? t("admin.employees.employeeDialog.title.edit")
          : t("admin.employees.employeeDialog.title.add")}
        {employee && (
          <Button
            sx={{ float: "right", marginTop: "-2px" }}
            color="primary"
            onClick={handleOpenEmpDetails} // your handler function
          >
            {t("admin.employees.employeeDialog.employeeDetails.title")}
          </Button>
        )}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            mb={2}
          >
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="avatar-upload"
              onChange={handleFileChange}
            />
            <label htmlFor="avatar-upload" style={{ position: "relative", display: "block" }}>
              {/* Avatar Wrapper */}
              <Box
                sx={{
                  width: { xs: 80, sm: 100, md: 120 }, // Responsive avatar size
                  height: { xs: 80, sm: 100, md: 120 },
                  borderRadius: "50%",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.9, // Subtle hover effect
                    },
                  }}
                >
                  {/* Employee Avatar */}
                  <EmployeeAvatar
                    photo={formik.values.photo}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                  {/* Edit Icon */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 15, // Position at the bottom-right
                      right: 15,
                      backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent white background
                      borderRadius: "50%",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)", // Subtle shadow
                    }}
                  >
                    <Edit /> {/* Small edit icon */}
                  </Box>
                </Box>
              </Box>
            </label>
          </Box>
          {/* WRAPPER BOX for the button */}
          {employee && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => setResetPasswordConfirmOpen(true)} // your function
              >
                {t("admin.employees.employeeDialog.fields.resetPas")}
              </MDButton>
            </Box>
          )}

          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.fields.name") + " *"}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.fields.email") + " *"}
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.fields.phone")}
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.fields.employeeId")}
            name="referencedEmployeeId"
            value={formik.values.referencedEmployeeId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.referencedEmployeeId && Boolean(formik.errorsreferencedEmployeeId)
            }
            helperText={formik.touched.referencedEmployeeId && formik.errors.referencedEmployeeId}
            margin="normal"
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t("admin.employees.employeeDialog.fields.dob")}
              value={formik.values.dob ? dayjs(formik.values.dob) : null}
              onChange={(newValue) => formik.setFieldValue("dob", newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  error={formik.touched.dob && Boolean(formik.errors.dob)}
                  helperText={formik.touched.dob && formik.errors.dob}
                />
              )}
            />
          </LocalizationProvider>
          <TextField
            fullWidth
            select
            label={t("admin.employees.employeeDialog.fields.employmentType") + " *"}
            name="employmentType"
            value={formik.values.employmentType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.employmentType && Boolean(formik.errors.employmentType)}
            helperText={formik.touched.employmentType && formik.errors.employmentType}
            margin="normal"
            sx={fieldStyles}
          >
            {[
              { id: "FULL_TIME", name: "Full Time" },
              { id: "PART_TIME", name: "Part Time" },
            ].map((x) => (
              <MenuItem key={x.id} value={x.id}>
                {x.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label={t("admin.employees.employeeDialog.fields.role") + " *"}
            name="isAdmin"
            value={formik.values.isAdmin}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.isAdmin && Boolean(formik.errors.isAdmin)}
            helperText={formik.touched.isAdmin && formik.errors.isAdmin}
            margin="normal"
            sx={fieldStyles}
          >
            {[
              { id: true, name: "Admin" },
              { id: false, name: "User" },
            ].map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label={t("admin.employees.employeeDialog.fields.gender") + " *"}
            name="gender"
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.gender && Boolean(formik.errors.gender)}
            helperText={formik.touched.gender && formik.errors.gender}
            margin="normal"
            sx={fieldStyles}
          >
            <MenuItem value="Male">{t("admin.employees.employeeDialog.genders.male")}</MenuItem>
            <MenuItem value="Female">{t("admin.employees.employeeDialog.genders.female")}</MenuItem>
          </TextField>

          <Autocomplete
            options={branches}
            getOptionLabel={(option) => option.branchName}
            value={branches?.find((b) => b.branchId === formik.values.branch) || null}
            onChange={(event, newValue) =>
              formik.setFieldValue("branch", newValue ? newValue.branchId : "")
            }
            onBlur={formik.handleBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.employees.employeeDialog.fields.branch") + " *"}
                name="branch"
                error={formik.touched.branch && Boolean(formik.errors.branch)}
                helperText={formik.touched.branch && formik.errors.branch}
                margin="normal"
                fullWidth
                sx={fieldStyles}
              />
            )}
          />
          <Autocomplete
            options={jobs}
            getOptionLabel={(option) => option.jobName}
            value={jobs?.find((j) => j.jobId === formik.values.job) || null}
            onChange={(event, newValue) =>
              formik.setFieldValue("job", newValue ? newValue.jobId : "")
            }
            onBlur={formik.handleBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.employees.employeeDialog.fields.job") + " *"}
                name="job"
                error={formik.touched.job && Boolean(formik.errors.job)}
                helperText={formik.touched.job && formik.errors.job}
                margin="normal"
                fullWidth
                sx={fieldStyles}
              />
            )}
          />
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.fields.department")}
            name="department"
            value={department?.departmentName}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled
          />
          {formik.values.job && (
            <Autocomplete
              options={jobGroups}
              getOptionLabel={(option) => option.jobGroup}
              loading={loadingJobGroups}
              value={jobGroups?.find((j) => j.jobGroupId === formik.values.jobGroup) || null}
              onChange={(event, newValue) =>
                formik.setFieldValue("jobGroup", newValue ? newValue.jobGroupId : "")
              }
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.employees.filters.jobGroup")}
                  name="jobGroup"
                  error={formik.touched.jobGroup && Boolean(formik.errors.jobGroup)}
                  helperText={formik.touched.jobGroup && formik.errors.jobGroup}
                  margin="normal"
                  fullWidth
                  sx={fieldStyles}
                />
              )}
            />
          )}

          <TextField
            fullWidth
            select
            label={t("admin.employees.employeeDialog.fields.directManager")}
            name="directManager"
            value={formik.values.directManager}
            error={formik.touched.directManager && Boolean(formik.errors.directManager)}
            helperText={formik.touched.directManager && formik.errors.directManager}
            onChange={formik.handleChange}
            margin="normal"
            disabled={!(directManagers.length > 0)}
            sx={fieldStyles}
          >
            {directManagers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {manager.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label={t("admin.employees.employeeDialog.fields.indirectManager")}
            name="indirectManager"
            value={formik.values.indirectManager}
            error={formik.touched.indirectManager && Boolean(formik.errors.indirectManager)}
            helperText={formik.touched.indirectManager && formik.errors.indirectManager}
            onChange={formik.handleChange}
            margin="normal"
            disabled={!(indirectManagers.length > 0)}
            sx={fieldStyles}
          >
            {indirectManagers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {manager.name}
              </MenuItem>
            ))}
          </TextField>
        </form>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000} // Show for 2 seconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleSnackbarClose} severity={severity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </DialogContent>

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            {t("admin.employees.employeeDialog.buttons.cancel")}
          </Button>
          {employee && (
            <Button
              onClick={() => setConfirmOpen(true)}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.employees.employeeDialog.buttons.delete")}
            </Button>
          )}
          <Button
            onClick={formik.handleSubmit}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {employee
              ? t("admin.employees.employeeDialog.buttons.update")
              : t("admin.employees.employeeDialog.buttons.add")}
          </Button>
        </DialogActions>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={confirmClose}
        onConfirm={async () => {
          confirmClose();
          await onDelete(employee.id);
          handleClose();
        }}
      />

      <ConfirmationDialog
        open={resetPasswordConfirmOpen}
        onClose={() => !resetLoading && setResetPasswordConfirmOpen(false)}
        onConfirm={handleResetPassword}
        confirmDisabled={resetLoading}
        loading={resetLoading}
      />
    </Dialog>
  );
};

AddEmployeeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  jobs: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  branches: PropTypes.array.isRequired,
  employee: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  departments: PropTypes.array.isRequired,
  defaultJobId: PropTypes.number,
};

export default AddEmployeeDialog;
